import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { cva } from "class-variance-authority";
import { VFC } from "react";

import { cn } from "../../../utils/utils";

type variant = "primary" | "outlined" | "alert";
type Size = "small" | "medium" | "large";

interface Props {
  text: string;
  variant?: variant;
  disabled?: boolean;
  isFullWidth?: boolean;
  size?: Size;
  onClick?: () => void;
  openInNewTab?: boolean;
  className?: string;
  id?: string;
}

/**
 * Common button component.
 *
 * Styling is token-driven via `cva` (see src/styles/tokens.cjs) + `cn`.
 * The public Props API and rendered DOM are intentionally unchanged.
 *
 * @param text button text
 * @param id id
 * @param variant kind of button
 * @param disabled disabled flag
 * @param isFullWidth if you need 100% width, use this flag
 * @param size button size
 * @param onClick on click function
 * @param openInNewTab if you need open in new tab, use this flag
 */
const buttonVariants = cva(
  "inline-flex justify-center items-center content-center rounded-md font-semibold border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary border-primary text-white hover:bg-button-hover hover:border-button-hover",
        outlined:
          "bg-white border-primary text-primary hover:text-button-hover hover:border-button-hover",
        alert:
          "bg-white border-error text-error hover:bg-error hover:text-white",
      },
      size: {
        small: "px-3 py-1 text-xs",
        medium: "px-6 py-2 text-base",
        large: "px-8 py-3 text-xl",
        // matches the previous default of padding 8px 16px / 16px font
        default: "px-4 py-2 text-base",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  }
);

const Button: VFC<Props> = (props: Props) => {
  // Props.size is optional; fall back to the internal "default" size.
  const size = props.size ?? "default";

  return (
    <button
      className={cn(
        buttonVariants({ variant: props.variant ?? "primary", size }),
        props.isFullWidth && "w-full",
        props.className
      )}
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      id={props.id}
    >
      {props.openInNewTab && <OpenInNewIcon className="mr-2" />}
      {props.text}
    </button>
  );
};

export default Button;
